import { A, useLocation, useNavigate } from "@solidjs/router";
import { createSignal, For, type JSX, Match, Show, Switch } from "solid-js";
import { Dynamic } from "solid-js/web";
import { readPreview, writePreview } from "../../api/client.ts";
import type { Me } from "../../api/domain.ts";
import { useApi } from "../../api/provider.tsx";
import {
	type ApiError,
	describeError,
	isUnauthorized,
} from "../../api/result.ts";
import {
	Actions,
	Button,
	Form,
	IconButton,
	TextField,
} from "../../components/Form.tsx";
import { ChevronLeftIcon, MenuIcon } from "../../components/icons.tsx";
import { LanguagePicker } from "../../components/LanguagePicker.tsx";
import { type NavKey, navIcons } from "../../components/nav-icons.tsx";
import { useI18n } from "../../i18n/provider.tsx";
import { createLoader, loadedValue } from "../../lib/async.ts";
import { SessionProvider, type Session } from "../../session/provider.tsx";
import styles from "./admin.module.scss";

/** The breakpoint the stylesheet turns the rail into the page top at. */
const wideEnoughForRail = (): boolean =>
	typeof window === "undefined" ||
	window.matchMedia("(min-width: 60.0625rem)").matches;

/** Whether the browser already holds a live session, asked at `/me`. */
type Access = "checking" | "granted" | "denied" | { failed: ApiError };

type SignInProps = {
	onGranted: () => void;
};

const SignIn = (props: SignInProps) => {
	const api = useApi();
	const i18n = useI18n();
	const [token, setToken] = createSignal("");
	const [busy, setBusy] = createSignal(false);
	const [problem, setProblem] = createSignal<string | undefined>(undefined);

	const submit = async () => {
		setBusy(true);
		setProblem(undefined);
		const result = await api.login(token().trim());
		setBusy(false);
		if (!result.ok) {
			return setProblem(
				isUnauthorized(result.error)
					? i18n.t().auth.refused
					: describeError(i18n.t(), result.error),
			);
		}
		setToken("");
		return props.onGranted();
	};

	return (
		<section class={styles.signin}>
			<div class={styles.heading}>
				<h1>{i18n.t().auth.title}</h1>
			</div>
			<Form>
				<TextField
					label={i18n.t().auth.token}
					value={token()}
					problem={problem()}
					type="password"
					onInput={setToken}
				/>
				<Actions>
					<Button
						label={busy() ? i18n.t().auth.signingIn : i18n.t().auth.signIn}
						tone="primary"
						disabled={busy() || token().trim() === ""}
						onClick={() => void submit()}
					/>
					<LanguagePicker />
				</Actions>
			</Form>
		</section>
	);
};

type Link = { href: string; label: string; icon: NavKey };

export type AdminShellProps = {
	children: JSX.Element;
};

/**
 * The session gate and the frame around every screen. `/me` says who is signed
 * in; the answer picks the rail. An admin gets every door, a customer user or
 * a previewing admin gets the six a customer needs, and the preview carries a
 * banner so a consultant always knows whose world is on screen.
 */
export const AdminShell = (props: AdminShellProps) => {
	const api = useApi();
	const i18n = useI18n();
	const location = useLocation();
	const navigate = useNavigate();
	const [signedOut, setSignedOut] = createSignal(false);
	/*
	 * Below the breakpoint the rail is the top of the page rather than a column
	 * beside it, so an open one is the entire first screen and the page under it
	 * reads as empty. It starts folded there and stays wherever it is put.
	 */
	const [railOpen, setRailOpen] = createSignal(wideEnoughForRail());
	const probe = createLoader(() => api.me());
	const me = (): Me | undefined => loadedValue(probe.value());

	const access = (): Access => {
		if (signedOut()) return "denied";

		const state = probe.value();
		if (state.state === "loading") return "checking";
		if (state.state === "loaded") return "granted";
		return isUnauthorized(state.error) ? "denied" : { failed: state.error };
	};

	const isCustomer = (): boolean => me()?.role === "customer";
	const previewing = (): boolean => me()?.preview === true;

	const adminLinks = (): Link[] => [
		{ href: "/", label: i18n.t().nav.dashboard, icon: "dashboard" },
		{ href: "/catalog", label: i18n.t().nav.catalog, icon: "catalog" },
		{ href: "/products", label: i18n.t().nav.products, icon: "products" },
		{ href: "/personas", label: i18n.t().nav.personas, icon: "personas" },
		{
			href: "/questionnaires",
			label: i18n.t().nav.questionnaires,
			icon: "questionnaires",
		},
		{ href: "/authors", label: i18n.t().nav.authors, icon: "authors" },
		{ href: "/customers", label: i18n.t().nav.customers, icon: "customers" },
		{ href: "/views", label: i18n.t().nav.views, icon: "views" },
		{ href: "/overview", label: i18n.t().nav.overview, icon: "overview" },
		{ href: "/compare", label: i18n.t().nav.compare, icon: "compare" },
		{ href: "/reports", label: i18n.t().nav.reports, icon: "reports" },
	];

	const customerLinks = (): Link[] => [
		{ href: "/", label: i18n.t().nav.start, icon: "dashboard" },
		{ href: "/products", label: i18n.t().nav.products, icon: "products" },
		{ href: "/compare", label: i18n.t().nav.compare, icon: "compare" },
		{ href: "/reports", label: i18n.t().nav.reports, icon: "reports" },
		{ href: "/persona", label: i18n.t().nav.persona, icon: "personas" },
	];

	const links = (): Link[] => (isCustomer() ? customerLinks() : adminLinks());

	const signOut = async () => {
		setSignedOut(true);
		writePreview(undefined);
		await api.logout();
	};

	const enter = () => {
		setSignedOut(false);
		void probe.reload();
	};

	const session: Session = {
		me,
		canWrite: () => me()?.role === "admin" && !previewing(),
		isCustomer,
		enterPreview: (customer) => {
			writePreview(customer);
			navigate("/");
			void probe.reload();
		},
		exitPreview: () => {
			const returnTo = readPreview();
			writePreview(undefined);
			navigate(returnTo === undefined ? "/customers" : `/customers/${returnTo}`);
			void probe.reload();
		},
	};

	const failure = () => {
		const state = access();
		return typeof state === "object" ? state.failed : undefined;
	};

	return (
		<Switch>
			<Match when={failure()}>
				{(error) => (
					<section class={styles.signin} role="alert">
						<p class={styles.problem}>{describeError(i18n.t(), error())}</p>
						<Button
							label={i18n.t().common.retry}
							onClick={() => void probe.reload()}
						/>
					</section>
				)}
			</Match>
			<Match when={access() === "denied"}>
				<SignIn onGranted={enter} />
			</Match>
			<Match when={access() === "checking"}>
				<p class={styles.note}>{i18n.t().common.loading}</p>
			</Match>
			<Match when={access() === "granted"}>
				<SessionProvider value={session}>
					<div
						class={`${styles.shell} ${railOpen() ? "" : styles.shellFolded}`}
					>
						<nav
							class={`${styles.sidebar} ${railOpen() ? "" : styles.sidebarFolded}`}
							aria-label={i18n.t().app.name}
						>
							<div class={styles.railHead}>
								<Show when={railOpen()}>
									<div class={styles.brandBlock}>
										<span class={styles.brand}>{i18n.t().app.name}</span>
										<Show when={me()?.customer}>
											{(customer) => (
												<span class={styles.brandCustomer}>
													{customer().name}
												</span>
											)}
										</Show>
									</div>
								</Show>
								<IconButton
									label={
										railOpen() ? i18n.t().nav.collapse : i18n.t().nav.expand
									}
									icon={railOpen() ? <ChevronLeftIcon /> : <MenuIcon />}
									onClick={() => setRailOpen(!railOpen())}
								/>
							</div>
							<Show when={railOpen()}>
								<div class={styles.nav}>
									<For each={links()}>
										{(link) => (
											<A
												class={`${styles.link} ${location.pathname === link.href ? styles.linkActive : ""}`}
												href={link.href}
											>
												{/* The glyph is decoration: the label beside it already names the target. */}
												<Dynamic
													component={navIcons[link.icon]}
													class={styles.linkIcon}
													aria-hidden="true"
												/>
												{link.label}
											</A>
										)}
									</For>
								</div>
								<div class={styles.footer}>
									<Show when={me()?.name}>
										{(name) => <span class={styles.whoami}>{name()}</span>}
									</Show>
									<LanguagePicker />
									<Button
										label={i18n.t().nav.logout}
										icon={
											<Dynamic component={navIcons.logout} aria-hidden="true" />
										}
										onClick={() => void signOut()}
									/>
								</div>
							</Show>
						</nav>
						<div class={styles.content}>
							<Show when={previewing()}>
								<div class={styles.previewBanner} role="status">
									<span>
										{i18n.t().preview.viewing}{" "}
										<strong>{me()?.customer?.name}</strong>.{" "}
										{i18n.t().preview.readOnly}
									</span>
									<Button
										label={i18n.t().preview.exit}
										onClick={session.exitPreview}
									/>
								</div>
							</Show>
							{props.children}
						</div>
					</div>
				</SessionProvider>
			</Match>
		</Switch>
	);
};
